import { useState, useEffect, useCallback } from "react";
import api, { errMsg, imageSrc, limitInfo } from "@/lib/api";
import { EmptyState } from "@/components/EmptyState";
import { toast } from "@/components/ui/sonner";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Instagram, Linkedin, Twitter, Facebook, Trash2, Send, Loader2, ChevronLeft, ChevronRight, Copy, Download, Image as ImageIcon, CalendarClock, ImageOff, RotateCw } from "lucide-react";
import { copyCaption, exportGroup, exportGroups } from "@/lib/util";
import { PreviewErrorBoundary } from "@/components/PreviewErrorBoundary";
import { Eyebrow } from "@/components/Eyebrow";

const PLATFORMS = [
  { key: "instagram", label: "Instagram", Icon: Instagram },
  { key: "linkedin", label: "LinkedIn", Icon: Linkedin },
  { key: "twitter", label: "X / Twitter", Icon: Twitter },
  { key: "facebook", label: "Facebook", Icon: Facebook },
];

const actionBtn = {
  display: "flex", alignItems: "center", gap: 5, background: "transparent",
  border: "1px solid rgba(24, 24, 27,0.20)", color: "var(--tsecondary)",
  borderRadius: 6, padding: "5px 10px", fontSize: 11, cursor: "pointer",
};

function DevelopImage({ src, rendering, alt, height = 200, onRegenerate }) {
  const [loaded, setLoaded] = useState(false);
  const [errored, setErrored] = useState(false);
  const [bust, setBust] = useState(0);
  const done = !rendering && (loaded || !src);
  const finalSrc = src && bust ? `${src}${src.includes("?") ? "&" : "?"}r=${bust}` : src;

  const regenerate = (e) => {
    e?.stopPropagation?.();
    setErrored(false);
    setLoaded(false);
    setBust(Date.now());
    onRegenerate?.();
  };

  if (!rendering && src && errored) {
    return (
      <div data-testid="image-unavailable" style={{ width: "100%", height, background: "var(--input)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 10, borderRadius: 8, padding: 12, textAlign: "center" }}>
        <ImageOff size={22} style={{ color: "var(--tmuted)" }} />
        <span className="font-body" style={{ fontSize: 12, color: "var(--tsecondary)" }}>Image unavailable</span>
        <button data-testid="image-regenerate" className="font-mono" style={{ ...actionBtn, borderColor: "var(--orange)", color: "var(--orange)" }} onClick={regenerate}>
          <RotateCw size={12} /> Regenerate
        </button>
      </div>
    );
  }

  return (
    <div className={`develop ${done ? "done" : ""}`} style={{ width: "100%", height, background: "var(--input)" }}>
      <div className="wash" />
      {!rendering && finalSrc && (
        <img src={finalSrc} alt={alt} onLoad={() => setLoaded(true)} onError={() => setErrored(true)} style={{ width: "100%", height, objectFit: "cover" }} />
      )}
      <div className="font-mono" style={{ position: "absolute", bottom: 8, left: 10, fontSize: 11, color: "var(--orange)", zIndex: 4 }}>
        {rendering ? "RENDERING..." : loaded ? "COMPLETE" : "DEVELOPING..."}
      </div>
    </div>
  );
}

function AssetCarousel({ items, height = 200 }) {
  const [idx, setIdx] = useState(0);
  const list = items || [];
  const n = list.length;
  if (n === 0) return <DevelopImage src={null} rendering alt="" height={height} />;
  const cur = list[Math.min(idx, n - 1)];
  const go = (e, d) => { e.stopPropagation(); setIdx((p) => (p + d + n) % n); };
  return (
    <div style={{ position: "relative" }}>
      <DevelopImage key={cur.id} src={imageSrc(cur.url)} rendering={false} alt="" height={height} />
      {n > 1 && (
        <>
          <button className="carousel-nav" style={{ left: 8 }} onClick={(e) => go(e, -1)} data-testid="carousel-prev"><ChevronLeft size={15} /></button>
          <button className="carousel-nav" style={{ right: 8 }} onClick={(e) => go(e, 1)} data-testid="carousel-next"><ChevronRight size={15} /></button>
          <div style={{ position: "absolute", bottom: 8, right: 10, display: "flex", gap: 4, zIndex: 6 }}>
            {list.map((it, i) => (
              <span key={it.id ?? i} style={{ width: 6, height: 6, borderRadius: 999, background: i === idx ? "var(--orange)" : "rgba(255,255,255,0.35)" }} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function scheduleToast() {
  toast("Post scheduled 🎉", {
    duration: 3000,
    style: { background: "#141414", color: "#EDEDED", borderLeft: "3px solid #18181B", borderRadius: "8px" },
    className: "font-body",
  });
}

function AssetCard({ group, index, onOpen }) {
  const tilt = index % 2 === 0 ? 2 : -2.4;
  const rendering = group.status === "rendering";
  const multi = (group.items || []).length > 1;
  return (
    <div
      className="panel-card"
      data-testid={`rendered-image-${index}`}
      onClick={() => !rendering && onOpen(group)}
      style={{ padding: 8, cursor: rendering ? "default" : "pointer", transform: `rotate(${tilt}deg)`, transition: "transform 0.25s ease", overflow: "hidden" }}
      onMouseEnter={(e) => { if (!rendering) e.currentTarget.style.transform = "rotate(0deg) scale(1.01)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.transform = `rotate(${tilt}deg)`; }}
    >
      <div style={{ borderRadius: 8, overflow: "hidden" }}>
        {rendering
          ? <DevelopImage src={null} rendering alt={group.title} height={200} />
          : <AssetCarousel items={group.items} height={200} />}
      </div>
      <div className="font-mono" style={{ fontSize: 10, color: "var(--tmuted)", marginTop: 8, display: "flex", justifyContent: "space-between" }}>
        <span>{group.assetType}{multi ? ` · ${group.items.length} slides` : ""}</span>
        <span>{group.aspect || ""}</span>
      </div>
      {!rendering && (
        <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
          <button data-testid={`copy-caption-asset-${index}`} className="font-mono" style={actionBtn} onClick={(e) => { e.stopPropagation(); copyCaption(group.caption); }}>
            <Copy size={12} /> Copy
          </button>
          <button data-testid={`export-asset-${index}`} className="font-mono" style={actionBtn} onClick={(e) => { e.stopPropagation(); exportGroup(group); }} title={multi ? "Download all slides as one ZIP" : "Download image"}>
            <Download size={12} /> {multi ? "Download All" : "Download"}
          </button>
        </div>
      )}
    </div>
  );
}

function CalendarTab({ scheduled, onRemove }) {
  if (scheduled.length === 0) {
    return (
      <EmptyState
        testId="calendar-empty"
        icon={CalendarClock}
        title="Nothing scheduled yet"
        hint="Open any rendered asset and hit Schedule to queue it. Your upcoming posts will appear here grouped by day."
      />
    );
  }
  const groupedByDate = scheduled.reduce((acc, p) => {
    const day = new Date(p.scheduledAt).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
    (acc[day] = acc[day] || []).push(p);
    return acc;
  }, {});
  return (
    <div style={{ marginTop: 12 }} data-testid="calendar-list">
      {Object.entries(groupedByDate).map(([day, posts]) => (
        <div key={day} style={{ marginBottom: 18 }}>
          <div className="font-mono cline-b" style={{ fontSize: 11, color: "var(--orange)", paddingBottom: 6, marginBottom: 10, letterSpacing: "0.05em" }}>
            {day.toUpperCase()}
          </div>
          {posts.map((p) => (
            <div key={p.id} className="panel-card" data-testid={`scheduled-post-${p.id}`} style={{ padding: 10, marginBottom: 10, display: "flex", gap: 10 }}>
              <img src={imageSrc(p.imageUrl)} alt="" onError={(e) => { e.currentTarget.style.visibility = "hidden"; }} style={{ width: 48, height: 48, borderRadius: 6, objectFit: "cover", flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="font-body" style={{ fontSize: 12, color: "var(--tsecondary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {p.caption || "(no caption)"}
                </div>
                <div className="font-mono" style={{ fontSize: 10, color: "var(--tmuted)", marginTop: 4 }}>
                  {new Date(p.scheduledAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} · {(p.platforms || []).join(", ") || "—"}
                </div>
              </div>
              <button onClick={() => onRemove(p.id)} data-testid={`delete-scheduled-${p.id}`} style={{ background: "transparent", border: "none", color: "var(--tmuted)", cursor: "pointer" }}>
                <Trash2 size={15} />
              </button>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

function ScheduleDialog({ active, onClose, onScheduled, onLimit }) {
  const [caption, setCaption] = useState("");
  const [platforms, setPlatforms] = useState(["instagram"]);
  const [when, setWhen] = useState("");
  const [posting, setPosting] = useState(false);

  useEffect(() => {
    if (!active) return;
    setCaption(active.caption || "");
    setPlatforms(["instagram"]);
    const d = new Date(Date.now() + 24 * 3600 * 1000);
    d.setMinutes(0, 0, 0);
    setWhen(new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16));
  }, [active]);

  const togglePlatform = (k) =>
    setPlatforms((prev) => (prev.includes(k) ? prev.filter((p) => p !== k) : [...prev, k]));

  const schedule = async () => {
    if (!active) return;
    if (!when) { toast("Pick a date & time"); return; }
    if (platforms.length === 0) { toast("Select at least one platform"); return; }
    setPosting(true);
    try {
      const cover = (active.items && active.items[0]) || {};
      const { data } = await api.post("/schedule", {
        imageId: cover.id,
        imageUrl: imageSrc(cover.url),
        caption,
        platforms,
        scheduledAt: new Date(when).toISOString(),
      });
      if (data.via === "publer") {
        toast.success("Scheduled to Publer 🎉", { description: "Pushed to your connected accounts." });
      } else {
        scheduleToast();
        if (data.note) toast(data.note, { duration: 5000 });
      }
      onScheduled();
    } catch (e) {
      const info = limitInfo(e);
      if (info) onLimit?.(info);
      else toast.error(errMsg(e, "Could not schedule"));
    } finally {
      setPosting(false);
    }
  };

  const slides = (active?.items || []).length;

  return (
    <Dialog open={!!active} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="thin-scroll" style={{ background: "var(--panel)", border: "1px solid var(--line)", maxWidth: 460, maxHeight: "88vh", overflowY: "auto" }} data-testid="schedule-dialog">
        {active && (
          <PreviewErrorBoundary onReset={onClose}>
          <div>
            <DialogTitle className="sr-only">Schedule Post</DialogTitle>
            <DialogDescription className="sr-only">Edit the caption, choose platforms and a time, then schedule this asset.</DialogDescription>
            <Eyebrow>Schedule Post</Eyebrow>
            <div style={{ borderRadius: 8, overflow: "hidden", marginTop: 14, marginBottom: 10, border: "1px solid var(--cardborder)" }}>
              <AssetCarousel items={active.items || []} height={300} />
            </div>
            {slides > 1 && (
              <div className="font-mono" style={{ fontSize: 10, color: "var(--tmuted)", marginBottom: 12 }}>
                Carousel · {slides} slides — cover image will be scheduled
              </div>
            )}

            <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
              <button data-testid="dialog-copy-caption" className="font-mono" style={actionBtn} onClick={() => copyCaption(caption)}>
                <Copy size={12} /> Copy Caption
              </button>
              <button data-testid="dialog-export" className="font-mono" style={actionBtn} onClick={() => exportGroup({ ...active, caption })} title={slides > 1 ? "Download all slides as one ZIP" : "Download image"}>
                <Download size={12} /> Download {slides > 1 ? "All" : ""}
              </button>
            </div>

            <label className="font-mono" style={{ fontSize: 10, color: "var(--tmuted)", textTransform: "uppercase", letterSpacing: "0.08em" }}>Caption</label>
            <textarea data-testid="dialog-caption" className="dim-input font-body" style={{ marginTop: 6, minHeight: 80 }} value={caption} onChange={(e) => setCaption(e.target.value)} />

            <label className="font-mono" style={{ fontSize: 10, color: "var(--tmuted)", textTransform: "uppercase", letterSpacing: "0.08em", display: "block", marginTop: 14, marginBottom: 8 }}>Platforms</label>
            <div style={{ display: "flex", gap: 10 }}>
              {PLATFORMS.map(({ key, label, Icon }) => {
                const on = platforms.includes(key);
                return (
                  <button
                    key={key}
                    title={label}
                    data-testid={`platform-${key}`}
                    onClick={() => togglePlatform(key)}
                    style={{
                      width: 40, height: 40, borderRadius: 8, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                      background: on ? "rgba(24, 24, 27,0.12)" : "transparent",
                      border: `1px solid ${on ? "var(--orange)" : "rgba(24, 24, 27,0.20)"}`,
                      color: on ? "var(--orange)" : "var(--tsecondary)",
                    }}
                  >
                    <Icon size={17} />
                  </button>
                );
              })}
            </div>

            <label className="font-mono" style={{ fontSize: 10, color: "var(--tmuted)", textTransform: "uppercase", letterSpacing: "0.08em", display: "block", marginTop: 14, marginBottom: 6 }}>Date &amp; Time</label>
            <input data-testid="dialog-datetime" type="datetime-local" className="dim-input font-mono" value={when} onChange={(e) => setWhen(e.target.value)} />

            <button data-testid="schedule-submit" className="btn-primary" style={{ width: "100%", marginTop: 18, padding: 12 }} onClick={schedule} disabled={posting}>
              {posting ? <Loader2 size={15} className="spin" /> : <Send size={15} />}
              {posting ? "Scheduling..." : "Schedule Post"}
            </button>
          </div>
          </PreviewErrorBoundary>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function PreviewPublishPanel({ images, onLimit, refreshUsage, view = "preview", reloadKey = 0 }) {
  const [scheduled, setScheduled] = useState([]);
  const [active, setActive] = useState(null);

  const loadSchedule = useCallback(() => {
    api.get("/schedule").then(({ data }) => setScheduled(data.posts || [])).catch(() => {});
  }, []);

  useEffect(() => { loadSchedule(); }, [loadSchedule, reloadKey]);

  const removeScheduled = async (id) => {
    try {
      await api.delete(`/schedule/${id}`);
      loadSchedule();
    } catch (e) {
      toast.error(errMsg(e, "Could not delete"));
    }
  };

  const anyRendering = images.some((i) => i.status === "rendering");
  const doneGroups = images.filter((g) => g.status !== "rendering" && (g.items || []).length > 0);

  const exportAll = () => exportGroups(doneGroups);
  const copyAll = () => {
    const caps = doneGroups.map((g) => g.caption).filter(Boolean).join("\n\n");
    copyCaption(caps || "");
  };

  return (
    <div className={`thin-scroll ${anyRendering ? "grid-pulse" : ""}`} data-testid="preview-publish-panel">
      {view === "calendar" ? (
        <CalendarTab scheduled={scheduled} onRemove={removeScheduled} />
      ) : (
        <>
          {doneGroups.length > 0 && (
            <div
              data-testid="quick-actions-bar-preview"
              style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 14, padding: "9px 12px", borderRadius: 10, border: "1px solid var(--cardborder)", background: "var(--panel)" }}
            >
              <span className="font-mono" style={{ fontSize: 10, color: "var(--tmuted)", letterSpacing: "0.04em" }}>
                {doneGroups.length} ASSET{doneGroups.length > 1 ? "S" : ""} READY
              </span>
              <div style={{ display: "flex", gap: 8 }}>
                <button data-testid="copy-all-captions" className="font-mono" style={actionBtn} onClick={copyAll} title="Copy all captions">
                  <Copy size={12} /> Copy All
                </button>
                <button data-testid="export-all-assets" className="font-mono" style={actionBtn} onClick={exportAll} title="Download every rendered asset as one ZIP">
                  <Download size={12} /> Download All
                </button>
              </div>
            </div>
          )}
          {images.length === 0 && (
            <EmptyState
              testId="preview-empty"
              icon={ImageIcon}
              title="No assets rendered yet"
              hint='Write a creative direction and generate ideas above, then hit "Render Assets" on a concept. Your finished visuals appear here ready to schedule.'
            />
          )}
          <div style={{ display: "flex", flexDirection: "column", gap: 22 }} data-testid="rendered-images">
            {images.map((group, i) => (
              <AssetCard key={group.tempId} group={group} index={i} onOpen={setActive} />
            ))}
          </div>
        </>
      )}

      <ScheduleDialog
        active={active}
        onClose={() => setActive(null)}
        onLimit={onLimit}
        onScheduled={() => { setActive(null); loadSchedule(); refreshUsage?.(); }}
      />
    </div>
  );
}
